import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';

import { Link } from "react-router-dom";

import fondo from '../../assets/images/fondo2.jpg';
import compu from '../../assets/images/computadora.png';

function MainContent() {
  const [index, setIndex] = useState(0);
  const [showWarning, setShowWarning] = useState(false);
  const [showConsentModal, setShowConsentModal] = useState(true);
  const [acceptMarketing, setAcceptMarketing] = useState(true);
  const [acceptTerms, setAcceptTerms] = useState(false);

  const intervalRef = useRef(null);
  const navigate = useNavigate();
  const { user, rol } = useAuth();

  const slides = [
    {
      title: 'Bienvenidos al Núcleo de Apoyo Fiscal - ESPE',
      content: `El NAF ESPE ofrece orientación gratuita sobre temas contables y tributarios. 
                Nuestro objetivo es contribuir con la sociedad brindando apoyo profesional desde la formación estudiantil.`,
      extra: `Aquí encontrarás recursos informativos, boletines actualizados, acceso a seminarios, asesorías y mucho más.`,
      showButton: false,
    },
    {
      title: 'Consultoría y Asesoría Virtual Gratuita',
      content: `Ahora puedes acceder a nuestras asesorías de forma 100% virtual desde la comodidad de tu hogar.`,
      extra: `Haz clic en el siguiente botón para solicitar tu asesoría personalizada.`,
      showButton: true,
    },
  ];

  const startInterval = () => {
    clearInterval(intervalRef.current);
    intervalRef.current = setInterval(() => {
      setIndex((prev) => (prev + 1) % slides.length);
    }, 15000);
  };

  useEffect(() => {
    const consentGiven = localStorage.getItem('naf_consent');
    if (consentGiven === 'true') {
      setShowConsentModal(false);
    }
    startInterval();
    return () => clearInterval(intervalRef.current);
  }, []);

  const handleServiciosClick = () => {
    if (!user) {
      navigate('/servicios');
    } else {
      if (rol === 1) navigate('/dashboard-admin/servicios');
      else if (rol === 2) navigate('/dashboard-editor/servicios');
      else if (rol === 3) navigate('/dashboard-usuario/servicios');
      else navigate('/');
    }
  };

  const goToNext = () => {
    setIndex((prev) => (prev + 1) % slides.length);
    startInterval();
  };

  const goToPrev = () => {
    setIndex((prev) => (prev - 1 + slides.length) % slides.length);
    startInterval();
  };

  const currentSlide = slides[index];

  return (
    <main
      className="main py-10 px-4 md:px-8 flex flex-col items-center justify-between min-h-screen"
      style={{
        backgroundImage: `url(${fondo})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
        backgroundAttachment: 'fixed',
      }}
    >
      {showConsentModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white w-full max-w-lg p-6 rounded-lg shadow-xl space-y-4 text-gray-800">
            <h2 className="text-xl font-bold text-center text-green-800">
              Política de Privacidad y Consentimiento
            </h2>
            <p className="text-sm text-justify">
              En NAF – ESPE, tratamos tus datos con el fin de gestionar nuestra relación como usuario del servicio de orientación tributaria y contable.
              Tienes derecho a acceder, rectificar y actualizar tus datos, así como a solicitar su supresión, limitación o eliminación. También tienes derecho a la portabilidad de tus datos cuando corresponda y a consultar el tratamiento realizado en cumplimiento de la normativa vigente de protección de datos personales.
              Si deseas ejercer estos derechos, puedes enviar un correo electrónico a: <strong>naf.espe@espe.edu.ec</strong>.
            </p>
            <div className="flex items-start space-x-2">
              <input
                type="checkbox"
                checked={acceptMarketing}
                onChange={() => setAcceptMarketing(!acceptMarketing)}
              />
              <span className="text-sm">
                Acepto recibir comunicaciones informativas sobre actividades académicas, capacitaciones y servicios relacionados con el proyecto NAF, a través de medios electrónicos.
              </span>
            </div>
            <div className="flex items-start space-x-2">
              <input
                type="checkbox"
                checked={acceptTerms}
                onChange={() => setAcceptTerms(!acceptTerms)}
              />
              <span className="text-sm">
                He leído y acepto los{" "}
                <Link to="/terminos-condiciones" className="underline font-semibold text-green-800 hover:text-green-900">
                  Términos y Condiciones
                </Link>{" "}
                y la{" "}
                <Link to="/politica-privacidad" className="underline font-semibold text-green-800 hover:text-green-900">
                  Política de Privacidad del Servicio
                </Link>.
              </span>

            </div>
            <button
              onClick={() => {
                if (acceptTerms) {
                  localStorage.setItem('naf_consent', 'true');
                  setShowConsentModal(false);
                } else {
                  alert('Debes aceptar los términos y condiciones.');
                }
              }}
              className="w-full mt-2 bg-green-700 hover:bg-green-600 text-white font-bold py-2 rounded"
            >
              ACEPTAR
            </button>
          </div>
        </div>
      )}

      <section className="w-full flex-grow">
        <div className="container mx-auto px-4 relative min-h-[420px]">
          <button
            onClick={goToPrev}
            className="hidden sm:block absolute left-4 sm:left-8 top-1/2 transform -translate-y-1/2 z-10 bg-white text-green-800 hover:text-white hover:bg-green-800 transition p-2 rounded-full shadow-lg"
          >
            <ChevronLeft size={28} />
          </button>

          <button
            onClick={goToNext}
            className="hidden sm:block absolute right-4 sm:right-8 top-1/2 transform -translate-y-1/2 z-10 bg-white text-green-800 hover:text-white hover:bg-green-800 transition p-2 rounded-full shadow-lg"
          >
            <ChevronRight size={28} />
          </button>

          <AnimatePresence mode="wait">
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 60 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -60 }}
              transition={{ duration: 1.5 }}
              className="bg-white/90 backdrop-blur-md rounded-3xl shadow-2xl w-full max-w-3xl mx-auto flex flex-col items-center p-6 md:p-10 text-center"
            >
              <img
                src={compu}
                alt="Logo NAF ESPE"
                className="w-36 sm:w-48 md:w-60 h-auto object-contain mb-4"
              />

              {/* Botones eliminados */}
              {/* {!user && (
                <div className="flex justify-center gap-4 mb-4">
                  <button
                    onClick={() => navigate('/login')}
                    className="px-6 py-2 border-2 border-green-700 text-green-700 hover:bg-green-700 hover:text-white font-semibold rounded-xl transition-all"
                  >
                    Iniciar Sesión
                  </button>
                  <button
                    onClick={() => navigate('/register')}
                    className="px-6 py-2 border-2 border-green-700 text-green-700 hover:bg-green-700 hover:text-white font-semibold rounded-xl transition-all"
                  >
                    Registrarse
                  </button>
                </div>
              )} */}

              <div className="text-gray-800">
                <h2 className="text-2xl sm:text-3xl md:text-4xl font-extrabold text-green-800 mb-4">
                  {currentSlide.title}
                </h2>
                <p className="text-base sm:text-lg md:text-xl leading-relaxed mb-3">
                  {currentSlide.content}
                </p>
                <p className="text-base sm:text-lg md:text-xl leading-relaxed mb-5">
                  {currentSlide.extra}
                </p>
                {currentSlide.showButton && (
                  <button
                    onClick={handleServiciosClick}
                    className="mt-4 px-6 py-3 bg-green-700 hover:bg-green-600 text-white font-semibold rounded-xl transition-all"
                  >
                    Solicitar una asesoría
                  </button>
                )}
                {showWarning && (
                  <p className="mt-4 text-red-700 font-semibold">
                    Debes iniciar sesión para acceder a los servicios.
                  </p>
                )}
              </div>
            </motion.div>
          </AnimatePresence>
        </div>
      </section>
    </main>
  );
}

export default MainContent;
